import React, { useEffect, useState } from 'react';
import 'styles/home.css';
import 'styles/articles.css';

import { SiteFooter, SiteNav, scrollToSection } from 'views/SiteChrome.js';
import QuickLookCta, { QuickLookRailLink } from 'views/QuickLookCta.js';
import { ArticleSkeleton, usePlaceholder } from 'views/Loading.js';
import { articleDate, loadArticle, relatedArticles, useArticles } from 'files/articles.js';
import { inline, parseArticle, richBlock } from 'files/markdown.js';

/* A single article.

   The piece and everything the header says about it arrive together from the
   server in one request, because the page cannot render either half on its own
   and two requests would mean a title appearing a beat before its prose. The
   trade is a fetch on open — hence the honest loading and failure states below.
   Never render a half-loaded article as though it were the whole thing.

   The index is fetched separately and only feeds "keep reading" at the foot, so
   it is allowed to arrive late or not at all. */
const Article = ({ active, utils }) => {

    const slug = active && active.slug;

    const [content, setContent] = useState(null);
    const [meta, setMeta] = useState(null);
    const [failed, setFailed] = useState(false);
    const [missing, setMissing] = useState(false);

    const library = useArticles();

    /* Held for a beat once shown. On a fast connection the response can land
       inside a hundred milliseconds, and a placeholder that appears and leaves
       inside that window is a flicker rather than a state. See Loading.js. */
    const waiting = usePlaceholder(!content && !failed);

    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => document.body.classList.remove('dl-body');
    }, []);

    useEffect(() => {

        let cancelled = false;
        setContent(null);
        setMeta(null);
        setFailed(false);
        setMissing(false);

        if(!slug) {
            setFailed(true);
            setMissing(true);
            return;
        }

        loadArticle(slug)
            .then(body => {
                if(!cancelled) {
                    setContent(parseArticle(body.content));
                    setMeta(body.article);
                }
            })
            .catch(e => {
                if(!cancelled) {
                    // a 404 is a slug we do not publish, which is a different
                    // thing to tell someone than a request that fell over
                    setMissing(String(e.message) === '404');
                    setFailed(true);
                }
            });

        return () => { cancelled = true; }

    }, [slug]);



    const go = (view, props) => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: view, ...props });
    }

    /* The contents links scroll in JS rather than as bare fragments, for the same
       reason the nav does: native fragment handling depends on when the page
       rendered and on which scroll container the browser picks. Doing it
       ourselves means one behaviour everywhere, smooth in both places. */
    const onSection = id => evt => {
        evt.preventDefault();
        scrollToSection(id);
    }

    /* The piece's own section headings, lifted from the parsed markdown. This is
       what earns the width on a wide screen: an eleven-minute guide read on a
       2000px display was one column of prose and nothing else, and no amount of
       centring fixes that — the layout needed a second element, not a different
       alignment. Short pieces get no rail; a contents list of two entries is
       furniture, not navigation. */
    const sections = content
        ? content.blocks
            .map((block, index) => ({ ...block, id: `section-${index}` }))
            .filter(block => block.type === 'h2')
        : [];

    const railed = sections.length >= 3;

    const getRail = () => {

        if(!railed) {
            return null;
        }
        return (
            <aside className={'ar-rail'}>
                {/* One bound box. The heading, every section and the Quick Look
                    line are all rows of the same list with the same padding, so
                    nothing in here is positioned relative to whatever happens to
                    sit above it. */}
                <nav className={'ar-rail-box'}>
                    <span className={'ar-rail-title'}>In this piece</span>
                    <ol className={'ar-rail-list'}>
                        {sections.map(section => (
                            <li key={section.id}>
                                <a href={`#${section.id}`} onClick={onSection(section.id)}>
                                    {section.text}
                                </a>
                            </li>
                        ))}
                        {/* worded from where this reader already is, the same
                            way the callout at the foot of the piece is */}
                        <QuickLookRailLink utils={utils} />
                    </ol>
                </nav>
            </aside>
        )
    }

    const getBody = () => {

        if(failed) {
            return (
                <div className={'ar-state'}>
                    <h2>We could not load this one.</h2>
                    <p>
                        {missing
                            ? 'That article is not one of ours. It may have been renamed.'
                            : 'The page failed to fetch it. Refreshing usually sorts it out.'}
                    </p>
                    <a className={'dl-btn dl-btn-outline'} href={'/articles'} onClick={go('articles')}>
                        See everything we have written <span className={'dl-arrow'}>→</span>
                    </a>
                </div>
            )
        }

        // unreachable while `waiting` is true, and `waiting` outlasts the fetch,
        // so by the time this renders there is either content or a failure
        if(!content) {
            return null;
        }

        return (
            <div className={'ar-body'}>
                {content.blocks.map((block, index) => {
                    if(block.type === 'h2') {
                        return <h2 id={`section-${index}`} key={index}>{inline(block.text)}</h2>;
                    }
                    if(block.type === 'h3') return <h3 key={index}>{inline(block.text)}</h3>;
                    // lists and tables render the same here as on a doc page
                    const rich = richBlock(block, index);
                    if(rich) return rich;
                    return <p key={index}>{inline(block.text)}</p>;
                })}
            </div>
        )
    }

    // derived from the library, never a hand-kept list on each article — see
    // relatedArticles in files/articles.js
    const more = relatedArticles(library.articles, slug, 3);

    return (
        <div className={'dl'}>
            <SiteNav utils={utils} />

            <article className={'ar-article'}>
                <div className={'dl-wrap'}>
                    <a className={'ar-back'} href={'/articles'} onClick={go('articles')}>← Articles</a>

                    {/* The placeholder stands in for the whole shell rather than
                        for the prose alone. Everything here arrives in one
                        response, so a header drawn while the body is still
                        empty would be a second layout on the way to the real
                        one, and two settling movements read worse than one. */}
                    {waiting && <ArticleSkeleton />}

                    {/* One grid for the whole piece, so the header and the prose
                        sit in the same column by construction rather than by two
                        max-widths that have to be kept in step. The rail is the
                        second column, beside the body. */}
                    {!waiting && (
                    <div className={`ar-shell ar-arrived ${railed ? 'is-railed' : ''}`}>
                        {meta && (
                            <header className={'ar-head'}>
                                <div className={'ar-meta'}>
                                    <span>{meta.topic}</span>
                                    <span>{articleDate(meta.published)}</span>
                                    <span>{meta.minutes} min read</span>
                                </div>
                                <h1 className={'dl-display'}>{meta.title}</h1>
                                <p className={'ar-dek'}>{meta.dek}</p>
                            </header>
                        )}

                        {getRail()}

                        <div className={'ar-prose'}>
                            {getBody()}

                            {content && content.callout && (
                                <QuickLookCta callout={content.callout} utils={utils} />
                            )}
                        </div>
                    </div>
                    )}
                </div>
            </article>

            {more.length > 0 && (
                <section className={'ar-more'}>
                    <div className={'dl-wrap'}>
                        <h2>Keep reading</h2>
                        <div className={'ar-more-list'}>
                            {more.map(article => (
                                <a
                                className={'ar-more-item'}
                                href={`/articles/${article.slug}`}
                                key={article.slug}
                                onClick={go('article', { slug: article.slug })}>
                                    <span className={'ar-more-topic'}>{article.topic}</span>
                                    <b>{article.title}</b>
                                    <span className={'ar-more-time'}>{article.minutes} min read</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <SiteFooter utils={utils} />
        </div>
    )
}

export default Article;
